import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, ChevronLeft, ChevronRight, ChevronDown, Radio } from "lucide-react";
import { api } from "@/lib/api";
import "./HomeWorldCupResults.css";

const MONTHS = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"];

function fmtDayMonth(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  return `${d} DE ${(MONTHS[m - 1] || "").toUpperCase()}`;
}

function matchHasFinished(m) { return m.status === "finished"; }

export default function HomeWorldCupResults() {
  const [data, setData] = useState(null);
  const [dayOffset, setDayOffset] = useState(0); // 0 = today, -1 = yesterday, etc
  const [openMenu, setOpenMenu] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/worldcup").then((r) => { if (!cancelled) setData(r.data); }).catch(() => {});
    load();
    // Poll every 30s to track live matches in real time
    const iv = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // Active live match (first one with status === 'live')
  const liveMatch = useMemo(() => {
    if (!data?.matches) return null;
    return data.matches.find((m) => m.status === "live") || null;
  }, [data]);

  const dates = useMemo(() => {
    if (!data?.matches) return [];
    return [...new Set(data.matches.map((m) => m.date))].sort();
  }, [data]);

  // "Today" in La Paz, Bolivia (UTC-4) — use Intl for correctness
  const today = useMemo(() => {
    try {
      return new Intl.DateTimeFormat("en-CA", { timeZone: "America/La_Paz" }).format(new Date());
    } catch {
      const now = new Date();
      const laPaz = new Date(now.getTime() - 4 * 60 * 60_000);
      return laPaz.toISOString().slice(0, 10);
    }
  }, []);
  const todayIdx = useMemo(() => {
    if (!dates.length) return 0;
    const fwd = dates.findIndex((d) => d >= today);
    return fwd < 0 ? dates.length - 1 : fwd;
  }, [dates, today]);
  const activeIdx = Math.min(Math.max(0, todayIdx + dayOffset), dates.length - 1);
  const activeDate = dates[activeIdx];

  const dayMatches = useMemo(() => {
    if (!data?.matches || !activeDate) return [];
    return data.matches.filter((m) => m.date === activeDate);
  }, [data, activeDate]);

  if (!data || !data.enabled || !data.matches?.length) return null;

  const dayLabel = activeDate === today ? "Partidos hoy" : `Partidos ${fmtDayMonth(activeDate).toLowerCase()}`;

  return (
    <section className="wcr-mod" data-testid="home-wc-results">
      {/* Header with sponsor strip */}
      <div className="wcr-head">
        <div className="wcr-head-left">
          <Trophy size={22} className="wcr-brand-icon" />
          <h2 className="wcr-title">MUNDIAL 2026</h2>
        </div>
        {data.sponsor_logo_url && (
          <img src={data.sponsor_logo_url} alt={data.sponsor_label || "Sponsor"} className="wcr-sponsor" />
        )}
      </div>

      {/* Live match hero (priority) — falls back to next match */}
      {liveMatch ? (
        <div className="wcr-live-hero" data-testid="wcr-live-hero">
          <div className="wcr-live-badge">
            <Radio size={12} className="wcr-live-radio" />
            <span>EN VIVO AHORA</span>
            {liveMatch.match_minute && <span className="wcr-live-minute">{liveMatch.match_minute}'</span>}
          </div>
          <div className="wcr-next-row">
            <div className="wcr-next-team">
              <img src={`https://flagcdn.com/w160/${liveMatch.home_flag}.png`} alt={liveMatch.home} />
              <p>{liveMatch.home}</p>
            </div>
            <div className="wcr-next-mid">
              <span className="wcr-live-score">
                {liveMatch.score_home ?? 0}
                <span className="wcr-live-dash">-</span>
                {liveMatch.score_away ?? 0}
              </span>
              <span className="wcr-next-group">GRUPO {liveMatch.group}</span>
            </div>
            <div className="wcr-next-team">
              <img src={`https://flagcdn.com/w160/${liveMatch.away_flag}.png`} alt={liveMatch.away} />
              <p>{liveMatch.away}</p>
            </div>
          </div>
          {(liveMatch.goals || []).length > 0 && (
            <div className="wcr-live-goals" data-testid="wcr-live-goals">
              <div className="wcr-live-goals-col wcr-live-goals-left">
                {(liveMatch.goals || []).filter((g) => g.team === "home").map((g, i) => (
                  <p key={`h${i}`}><span className="wcr-goal-dot">⚽</span> {g.player} <span className="wcr-goal-min">({g.minute})</span></p>
                ))}
              </div>
              <div className="wcr-live-goals-col wcr-live-goals-right">
                {(liveMatch.goals || []).filter((g) => g.team === "away").map((g, i) => (
                  <p key={`a${i}`}><span className="wcr-goal-dot">⚽</span> {g.player} <span className="wcr-goal-min">({g.minute})</span></p>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : data.next_match && (
        <div className="wcr-next" data-testid="wcr-next-match">
          <p className="wcr-next-label">PRÓXIMO PARTIDO</p>
          <div className="wcr-next-row">
            <div className="wcr-next-team">
              <img src={`https://flagcdn.com/w160/${data.next_match.home_flag}.png`} alt={data.next_match.home} />
              <p>{data.next_match.home}</p>
            </div>
            <div className="wcr-next-mid">
              <span className="wcr-next-vs">VS</span>
              <span className="wcr-next-when">{fmtDayMonth(data.next_match.date)} | {data.next_match.time_bol}</span>
              <span className="wcr-next-group">GRUPO {data.next_match.group}</span>
            </div>
            <div className="wcr-next-team">
              <img src={`https://flagcdn.com/w160/${data.next_match.away_flag}.png`} alt={data.next_match.away} />
              <p>{data.next_match.away}</p>
            </div>
          </div>
        </div>
      )}

      {/* Day selector */}
      <div className="wcr-day-bar">
        <button onClick={() => setDayOffset((d) => d - 1)} disabled={activeIdx <= 0} className="wcr-arrow" data-testid="wcr-prev-day">
          <ChevronLeft size={18} />
        </button>
        <div className="wcr-day-select">
          <button type="button" className="wcr-day-btn" onClick={() => setOpenMenu((o) => !o)} data-testid="wcr-day-toggle">
            <span>{dayLabel.toUpperCase()}</span>
            <ChevronDown size={16} className={openMenu ? "wcr-rot" : ""} />
          </button>
          {openMenu && (
            <ul className="wcr-day-menu" role="menu">
              {dates.map((d, i) => (
                <li key={d}>
                  <button
                    type="button"
                    className={`wcr-day-opt ${i === activeIdx ? "wcr-day-opt-active" : ""}`}
                    onClick={() => { setDayOffset(i - todayIdx); setOpenMenu(false); }}
                  >
                    {fmtDayMonth(d)} {d === today && "(hoy)"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button onClick={() => setDayOffset((d) => d + 1)} disabled={activeIdx >= dates.length - 1} className="wcr-arrow wcr-arrow-next" data-testid="wcr-next-day">
          <ChevronRight size={18} />
        </button>
      </div>

      <p className="wcr-day-count">{fmtDayMonth(activeDate)} · {dayMatches.length} {dayMatches.length === 1 ? "partido" : "partidos"}</p>

      {/* Matches list */}
      <div className="wcr-list">
        {dayMatches.length === 0 && (
          <p className="wcr-empty">No hay partidos programados este día.</p>
        )}
        {dayMatches.map((m) => {
          const finished = matchHasFinished(m);
          const goalsHome = (m.goals || []).filter((g) => g.team === "home");
          const goalsAway = (m.goals || []).filter((g) => g.team === "away");
          return (
            <article key={m.id} className={`wcr-match ${finished ? "wcr-match-finished" : ""}`} data-testid={`wcr-match-${m.id}`}>
              <div className="wcr-match-head">
                <span className="wcr-match-when">{fmtDayMonth(m.date)} | {m.time_bol}</span>
                <span className="wcr-match-group">GRUPO {m.group}</span>
              </div>
              <div className="wcr-match-body">
                <div className="wcr-side wcr-side-left">
                  <div className="wcr-team-info">
                    <p className="wcr-team-name">{m.home}</p>
                    {goalsHome.length > 0 && (
                      <ul className="wcr-goals">
                        {goalsHome.map((g, i) => (
                          <li key={i}><span className="wcr-goal-dot">⚽</span> {g.player} ({g.minute})</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <img src={`https://flagcdn.com/w160/${m.home_flag}.png`} alt={m.home} className="wcr-flag" />
                </div>
                <div className="wcr-score">
                  {finished ? (
                    <>
                      <span className="wcr-score-num">{m.score_home ?? 0} - {m.score_away ?? 0}</span>
                      <span className="wcr-score-label">FINAL</span>
                    </>
                  ) : m.status === "live" ? (
                    <>
                      <span className="wcr-score-num wcr-live">{m.score_home ?? 0} - {m.score_away ?? 0}</span>
                      <span className="wcr-score-label wcr-live-label">EN VIVO</span>
                    </>
                  ) : (
                    <>
                      <span className="wcr-score-num wcr-vs">VS</span>
                      <span className="wcr-score-label">{m.time_bol}</span>
                    </>
                  )}
                </div>
                <div className="wcr-side wcr-side-right">
                  <img src={`https://flagcdn.com/w160/${m.away_flag}.png`} alt={m.away} className="wcr-flag" />
                  <div className="wcr-team-info wcr-team-right">
                    <p className="wcr-team-name">{m.away}</p>
                    {goalsAway.length > 0 && (
                      <ul className="wcr-goals">
                        {goalsAway.map((g, i) => (
                          <li key={i}><span className="wcr-goal-dot">⚽</span> {g.player} ({g.minute})</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <div className="wcr-foot">
        <Link to="/categoria/deportes" className="wcr-link" data-testid="wcr-link-all">
          Ver fixture completo + grupos →
        </Link>
      </div>
    </section>
  );
}
